/**
 * Prompt Table
 * Lists all registered prompts with module, status, and last modified.
 */

import { Link } from 'react-router-dom';

export interface PromptListItem {
  key: string;
  module: string;
  name: string;
  description: string | null;
  isOverridden: boolean;
  currentVersion: number | null;
  updatedAt: string;
}

interface PromptTableProps {
  prompts: PromptListItem[];
  isLoading: boolean;
}

export default function PromptTable({ prompts, isLoading }: PromptTableProps) {
  if (isLoading) {
    return (
      <div className="border-2 border-black dark:border-gray-700 p-8 text-center">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-black dark:border-t-white rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="border-2 border-black dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
        Geen prompts gevonden.
      </div>
    );
  }

  return (
    <div className="border-2 border-black dark:border-gray-700 overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-black dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <th className="text-left px-4 py-3 font-bold text-black dark:text-white">Naam</th>
            <th className="text-left px-4 py-3 font-bold text-black dark:text-white">Module</th>
            <th className="text-left px-4 py-3 font-bold text-black dark:text-white">Status</th>
            <th className="text-left px-4 py-3 font-bold text-black dark:text-white">Laatst gewijzigd</th>
          </tr>
        </thead>
        <tbody>
          {prompts.map((prompt) => (
            <tr
              key={prompt.key}
              className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            >
              <td className="px-4 py-3">
                <Link
                  to={`/admin/prompts/${encodeURIComponent(prompt.key)}`}
                  className="font-medium text-black dark:text-white hover:underline"
                >
                  {prompt.name}
                </Link>
                <div className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-0.5">
                  {prompt.key}
                </div>
              </td>
              <td className="px-4 py-3">
                <span className="inline-block px-2 py-0.5 text-xs font-mono border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                  {prompt.module}
                </span>
              </td>
              <td className="px-4 py-3">
                {prompt.isOverridden ? (
                  <span className="inline-block px-2 py-0.5 text-xs font-bold border-2 border-green-600 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
                    Override actief
                  </span>
                ) : (
                  <span className="inline-block px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400">
                    Code default
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                {new Date(prompt.updatedAt).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
